/* Copyright (c) 2020-present CloudIO Inc */

import {
  chipVariant,
  iconButtonSize,
} from 'componentregistry/registry/constants';
import { toolTipProps } from 'componentregistry/utils/commonProps';
import { Platform } from '../../platform/Platform';
import { baseComponentManager } from '../../registry/Components';
import {
  ComponentManager,
  ComponentManagerFn,
  ViewAttributeChooserProp,
} from '../../registry/Component.type';
import { ItemType } from '../../registry/ComponentRegistry';
import { Chips, defaultValue } from './Chips';

const ChipsManager: ComponentManagerFn<'Chips'> = (
  platform: Platform,
): ComponentManager<'Chips'> => {
  return {
    ...baseComponentManager<'Chips'>(platform, 'Chips'),
    type: 'Chips',
    name: 'Chips',
    isChildAllowed: (page, item, childType) =>
      (['TriggerOnDelete'] as ItemType[]).includes(childType),
    defaultValue,
    onPropertyChange: (property, value, itemData) => {
      let val: Partial<Chips> = { [property]: value };
      if (property === 'delimiterCharacter' && typeof value === 'string') {
        if (value.length > 1) {
          val = { [property]: value.substr(0, 1) };
        }
      }
      return val;
    },
    props: {
      viewAttribute: {
        label: 'View Attribute',
        type: 'ViewAttributeChooser',
        category: 'Data',
        aliasType: 'STORE',
        getAttributeDataTypes: () => ['String'],
        preventMultiEdit: true,
      } as ViewAttributeChooserProp,
      delimiterCharacter: {
        label: 'Delimiter Character',
        type: 'string',
        helpText: 'Specify a delimiter e.g. | or ; or , etc.',
        category: 'Data',
        maxLength: 1,
      },
      disableCondition: {
        label: 'Disable Condition',
        type: 'expressionOnly',
        returnType: () => 'boolean',
      },
      color: {
        label: 'Color',
        type: 'ThemeBGColor',
        required: true,
        filterColor: (color: string) => {
          return ['default', 'primary', 'secondary'].includes(color);
        },
      },
      size: {
        label: 'Size',
        type: 'select',
        options: iconButtonSize,
      },
      variant: {
        label: 'Variant',
        type: 'select',
        options: chipVariant,
      },
      ...toolTipProps,
    },
    getDisplayText: (item) => item.props.viewAttribute ?? ' ',
    category: 'Data',
    icon: { iconName: 'rectangle-wide' },
    description: `Chips are compact elements that represent an input, attribute, or action. 
    Chips allow users to enter information, make selections, filter content, or trigger actions.
    While included here as a standalone component, the most common use will be in some form of input.`,
  };
};

export default ChipsManager;
